import {
  CHAT_V8_WIDGET_SNAPSHOT_VERSION,
  isChatV8PayloadKind,
  z_ChatV8WidgetArtifactSnapshotResponse,
  type T_ChatV8Event,
  type T_ChatV8WidgetArtifactSnapshotResponse,
} from "@lattice/core-lib/types/chat-v8";

import type { T_ChatV8ClientContext } from "./machine";
import type { T_ChatV8ClientTransport } from "./transport";

export type T_ChatV8WidgetSnapshotObserverState = {
  key: string | null;
  snapshots: Map<string, T_ChatV8WidgetArtifactSnapshotResponse>;
};

type T_WidgetSnapshotRequest = {
  fallback: T_ChatV8WidgetArtifactSnapshotResponse;
  widgetArtifactCuid: string;
};

type T_CreateChatV8WidgetSnapshotObserverArgs = {
  loadSnapshot: T_ChatV8ClientTransport["getWidgetArtifactSnapshot"];
  onChange: (state: T_ChatV8WidgetSnapshotObserverState) => void;
};

export type T_ChatV8WidgetSnapshotObserver = {
  dispose: () => void;
  inspect: (
    state: Pick<
      T_ChatV8ClientContext,
      "events" | "threadCuid" | "timelineVersion"
    >,
  ) => Promise<void>;
};

function collectSnapshotRequests(
  events: T_ChatV8Event[],
): T_WidgetSnapshotRequest[] {
  const requests = new Map<string, T_WidgetSnapshotRequest>();

  for (const event of events) {
    if (
      !isChatV8PayloadKind(event.payload, "message_part") ||
      event.payload.partKind !== "widget" ||
      !event.payload.widgetArtifactCuid
    ) {
      continue;
    }
    const widgetArtifactCuid = event.payload.widgetArtifactCuid;
    requests.set(widgetArtifactCuid, {
      fallback: {
        generatedAt: event.createdAt,
        modelContext: {
          facts: [],
          summary: "The widget snapshot could not be loaded.",
          title: `${event.payload.widgetType} widget`,
        },
        snapshot: {
          data: null,
          status: "unavailable",
          widgetType: event.payload.widgetType,
        },
        snapshotVersion: CHAT_V8_WIDGET_SNAPSHOT_VERSION,
        widgetArtifactCuid,
        widgetType: event.payload.widgetType,
      },
      widgetArtifactCuid,
    });
  }

  return Array.from(requests.values());
}

export function createChatV8WidgetSnapshotObserver({
  loadSnapshot,
  onChange,
}: T_CreateChatV8WidgetSnapshotObserverArgs): T_ChatV8WidgetSnapshotObserver {
  let disposed = false;
  let key: string | null = null;
  let snapshots = new Map<string, T_ChatV8WidgetArtifactSnapshotResponse>();
  const requestedArtifactCuids = new Set<string>();

  const publish = () => onChange({ key, snapshots: new Map(snapshots) });

  return {
    dispose: () => {
      disposed = true;
    },
    inspect: async (state) => {
      const nextKey =
        state.threadCuid === null || state.timelineVersion === null
          ? null
          : `${state.threadCuid}:${state.timelineVersion}`;
      if (nextKey !== key) {
        key = nextKey;
        snapshots = new Map();
        requestedArtifactCuids.clear();
        publish();
      }
      if (
        key === null ||
        state.threadCuid === null ||
        state.timelineVersion === null
      ) {
        return;
      }

      const requestKey = key;
      const threadCuid = state.threadCuid;
      const timelineVersion = state.timelineVersion;
      await Promise.all(
        collectSnapshotRequests(state.events).map(async (request) => {
          if (requestedArtifactCuids.has(request.widgetArtifactCuid)) return;
          requestedArtifactCuids.add(request.widgetArtifactCuid);

          let snapshot = request.fallback;
          try {
            const result = await loadSnapshot({
              threadCuid,
              timelineVersion,
              widgetArtifactCuid: request.widgetArtifactCuid,
            });
            const parsed =
              z_ChatV8WidgetArtifactSnapshotResponse.safeParse(result);
            if (parsed.success) snapshot = parsed.data;
          } catch {
            // The shared terminal fallback prevents either platform from
            // displaying a permanent loading card after a failed fetch.
          }

          if (disposed || key !== requestKey) return;
          snapshots.set(request.widgetArtifactCuid, snapshot);
          publish();
        }),
      );
    },
  };
}
